import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SelectOption } from '@chatsdet/ui';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { TestSuiteScheduleTarget, TestSuiteSchedulesResponse } from './types';
import type { SuiteAlertConfig } from './types';
import { usePropertySummaries } from '../../hooks/usePropertyMetadata';
import { fetchPropertyEnvironments } from './fetchPropertyEnvironments';

const SUITE_SCHEDULES_KEY = ['suite-schedules'] as const;

/**
 * List test-run schedules for the org. With `suiteId`, only schedules targeting
 * that named suite (the builder clock modal); without it, every schedule (the
 * Suites-list "Scheduled" section and the Test Runs "Upcoming" section).
 */
export function useSuiteSchedules(suiteId?: string, enabled = true) {
  return useQuery({
    queryKey: [...SUITE_SCHEDULES_KEY, suiteId ?? 'all'],
    enabled,
    queryFn: async (): Promise<TestSuiteSchedulesResponse> => {
      const url = suiteId
        ? `${API_URL}/test-suites/schedules?suiteId=${encodeURIComponent(suiteId)}`
        : `${API_URL}/test-suites/schedules`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load schedules');
      return res.json();
    },
  });
}

/**
 * Environments a schedule can be pinned to, across the org's applications.
 * Labelled "<Application> · <Environment>" only when there is more than one
 * application. Shares the `['property-environments', id]` cache with the
 * property tabs. Transient ephemeral environments are not offered.
 */
export function useScheduleEnvironmentOptions(enabled: boolean): SelectOption[] {
  const properties = usePropertySummaries({ enabled }).data ?? [];
  const results = useQueries({
    queries: properties.map((property) => ({
      queryKey: ['property-environments', property.id],
      enabled,
      staleTime: 60_000,
      queryFn: () => fetchPropertyEnvironments(property.id),
    })),
  });
  return properties.flatMap((property, index) =>
    (results[index]?.data?.items ?? [])
      // Ephemeral environments expire, so a schedule pinned to one would point at a dead environment.
      .filter((env) => env.type !== 'ephemeral')
      .map((env) => ({
        id: env.id,
        label: properties.length > 1 ? `${property.name} · ${env.name}` : env.name,
      }))
  );
}

/** What a new schedule targets: a named suite, a tag filter, or all eligible. */
export type SuiteScheduleTargetInput =
  | { target: 'suite'; suiteId: string }
  | { target: 'tag'; tags: string[] }
  | { target: 'all' };

/**
 * Create a recurring test-run schedule by minting a schedule-triggered
 * `workflow.run-tests` automation rule. The target shapes the rule's
 * actionConfig (`suite_id` / `tags` / neither).
 */
export function useCreateSuiteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cron: string;
      timezone: string;
      description: string;
      target: SuiteScheduleTargetInput;
      alertConfig: SuiteAlertConfig;
      /** External suites: scope the schedule to these area paths (omit/empty = every area). */
      externalAreas?: string[];
      /** Native suites: force this environment for the scheduled run (null = the suite's default). */
      environmentId: string | null;
    }) => {
      const actionConfig: Record<string, unknown> = { mode: 'run' };
      if (input.target.target === 'suite') {
        actionConfig.suite_id = input.target.suiteId;
      } else if (input.target.target === 'tag') {
        actionConfig.tags = input.target.tags;
      }
      if (input.externalAreas && input.externalAreas.length > 0) {
        actionConfig.external_areas = input.externalAreas;
      }
      if (input.environmentId) {
        actionConfig.environment_id = input.environmentId;
      }

      const res = await fetch(`${API_URL}/org/automation/rules`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: input.description,
          triggerSource: 'schedule',
          triggerEvent: 'scheduled',
          triggerConfig: {
            scheduleType: 'cron',
            scheduleConfig: { pattern: input.cron, timezone: input.timezone },
          },
          actionType: 'workflow.run-tests',
          actionConfig,
          alertConfig: input.alertConfig,
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create schedule');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SUITE_SCHEDULES_KEY }),
  });
}

/** Change one schedule's cadence in place; the API recomputes its next run. */
export function useUpdateScheduleTiming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ruleId,
      cron,
      timezone,
      description,
    }: {
      ruleId: string;
      cron: string;
      /** The schedule's own timezone, kept as-is; null (none stored) stays none. */
      timezone: string | null;
      description: string;
    }) => {
      const res = await fetch(`${API_URL}/org/automation/rules/${ruleId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: description,
          triggerConfig: {
            scheduleType: 'cron',
            scheduleConfig: { pattern: cron, timezone: timezone ?? undefined },
          },
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update schedule');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SUITE_SCHEDULES_KEY }),
  });
}

/** Update one schedule's own alert destinations, in place (separate from the target suite's alerts). */
export function useUpdateScheduleAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, alertConfig }: { ruleId: string; alertConfig: SuiteAlertConfig }) => {
      const res = await fetch(`${API_URL}/org/automation/rules/${ruleId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ alertConfig }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save schedule alerts');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SUITE_SCHEDULES_KEY }),
  });
}

/** Human label for a schedule's target (suite name / tag chips / all eligible). */
export function describeScheduleTarget(schedule: {
  target: TestSuiteScheduleTarget;
  suiteName: string | null;
  tags: string[];
}): string {
  switch (schedule.target) {
    case 'suite':
      return schedule.suiteName ?? 'Suite';
    case 'tag':
      return schedule.tags.length > 0 ? `Tagged: ${schedule.tags.join(', ')}` : 'Tagged';
    case 'all':
    default:
      return 'All published';
  }
}
